import { useEffect, useMemo, useState } from "react";
import Daily from "@daily-co/daily-js";
import {
  DailyAudio,
  DailyProvider,
  DailyVideo,
  useDaily,
} from "@daily-co/daily-react";
import {
  joinVideoConsultation,
  leaveVideoConsultation,
} from "../../api/patient/appointment";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./VideoCallPage.css";

const VideoCallShell = ({
  isJoining,
  error,
  participantsSnapshot,
  onLeave,
}) => {
  const callObject = useDaily();
  const participants = useMemo(
    () => {
      const participantsBySession = new Map();

      Object.values(participantsSnapshot || {}).forEach((participant) => {
        if (!participant?.session_id) return;
        participantsBySession.set(participant.session_id, participant);
      });

      return Array.from(participantsBySession.entries()).map(
        ([sessionId, participant]) => ({
          sessionId,
          participant,
        }),
      );
    },
    [participantsSnapshot],
  );
  const localParticipant = participants.find((item) => item.participant?.local);
  const remoteParticipants = participants.filter(
    (item) => !item.participant?.local,
  );

  const toggleMic = async () => {
    if (!callObject) return;
    await callObject.setLocalAudio(
      !(localParticipant?.participant?.audio || false),
    );
  };

  const toggleCamera = async () => {
    if (!callObject) return;
    await callObject.setLocalVideo(
      !(localParticipant?.participant?.video || false),
    );
  };

  return (
    <div className="video-call-page">
      <header className="video-call-header">
        <img src="/logo192.png" alt="MyHealthVaults" />
        <span>Secure Video Consultation</span>
      </header>
      <main className="video-call-body">
        {error ? (
          <div className="video-call-error">
            <h2>Unable to start the call</h2>
            <p>{error}</p>
          </div>
        ) : (
          <div className="video-call-frame">
            <DailyAudio />
            {isJoining && (
              <div className="video-call-loader">
                <div className="spinner" />
                <p>Connecting you to the consultation…</p>
              </div>
            )}

            <div className="video-call-grid">
              {remoteParticipants.length ? (
                remoteParticipants.map(({ sessionId, participant }) => (
                  <div className="video-call-tile" key={sessionId}>
                    <DailyVideo
                      fit="cover"
                      sessionId={sessionId}
                      type="video"
                    />
                    <div className="video-call-tile-label">
                      {participant?.user_name || "Doctor"}
                    </div>
                  </div>
                ))
              ) : (
                <div className="video-call-empty">
                  Waiting for the doctor to join.
                </div>
              )}
            </div>

            {localParticipant && (
              <div className="video-call-preview">
                <div className="video-call-tile">
                  <DailyVideo
                    automirror
                    fit="cover"
                    sessionId={localParticipant.sessionId}
                    type="video"
                  />
                  <div className="video-call-tile-label">You</div>
                </div>
              </div>
            )}

            <div className="video-call-controls">
              <button type="button" onClick={toggleMic}>
                {localParticipant?.participant?.audio ? "Mute" : "Unmute"}
              </button>
              <button type="button" onClick={toggleCamera}>
                {localParticipant?.participant?.video
                  ? "Camera Off"
                  : "Camera On"}
              </button>
              <button className="danger" type="button" onClick={onLeave}>
                Leave Call
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

const VideoCallPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const appointmentId =
    searchParams.get("appointmentId") ||
    searchParams.get("appointmetnId") ||
    "";
  const [isJoining, setIsJoining] = useState(true);
  const [error, setError] = useState("");
  const [participantsSnapshot, setParticipantsSnapshot] = useState({});
  // Daily throws synchronously ("WebRTC not supported or suppressed") when the
  // browser has no WebRTC — which is the case on every non-https origin except
  // localhost (the local dev stack at http://myhealthvaults.frontend included)
  // and in some embedded/old browsers. Creating it inside render with no guard
  // used to take the whole page down into the error boundary; now the page
  // explains the problem instead.
  const { callObject, setupError } = useMemo(() => {
    if (typeof window === "undefined") return { callObject: null, setupError: "" };
    if (window.isSecureContext === false) {
      return {
        callObject: null,
        setupError:
          "Video consultations need a secure (https) connection. Please open the app over https and try again.",
      };
    }
    if (!navigator.mediaDevices || typeof window.RTCPeerConnection !== "function") {
      return {
        callObject: null,
        setupError:
          "This browser doesn't support video calls. Please use a recent version of Chrome, Safari or Firefox.",
      };
    }
    try {
      return {
        callObject: Daily.createCallObject({
          subscribeToTracksAutomatically: true,
        }),
        setupError: "",
      };
    } catch (createError) {
      console.error("Daily.createCallObject failed:", createError);
      return {
        callObject: null,
        setupError:
          "Video calling isn't available in this browser. Please try another browser or device.",
      };
    }
  }, []);

  useEffect(() => {
    if (!callObject) {
      setError(setupError || "Video calling isn't available right now.");
      setIsJoining(false);
      return undefined;
    }
    if (!appointmentId) {
      setError("Appointment ID is missing.");
      setIsJoining(false);
      return undefined;
    }

    let isMounted = true;

    const syncParticipants = () => {
      if (!isMounted) return;

      try {
        setParticipantsSnapshot({ ...(callObject.participants() || {}) });
      } catch (eventError) {
        setParticipantsSnapshot({});
      }
    };

    const handleJoined = () => {
      if (!isMounted) return;
      setIsJoining(false);
      syncParticipants();
    };

    const handleLeft = () => {
      if (!isMounted) return;
      setIsJoining(false);
      navigate("/bookings", { replace: true });
    };

    const handleError = (event) => {
      if (!isMounted) return;
      setIsJoining(false);
      setError(
        event?.errorMsg || "Unable to start the call. Please try again.",
      );
    };

    callObject.on("joined-meeting", handleJoined);
    callObject.on("participant-joined", syncParticipants);
    callObject.on("participant-updated", syncParticipants);
    callObject.on("participant-left", syncParticipants);
    callObject.on("track-started", syncParticipants);
    callObject.on("track-stopped", syncParticipants);
    callObject.on("left-meeting", handleLeft);
    callObject.on("error", handleError);

    const initCall = async () => {
      const response = await joinVideoConsultation(appointmentId);
      const payload = response?.data?.data;

      // An expired session on a link opened hours later: send the patient to
      // log in and bring them straight back to the call, rather than showing
      // the generic failure (NABH COP.7.a).
      if (response?.status === 401) {
        navigate("/login", {
          replace: true,
          state: {
            from: {
              pathname: "/video-call",
              search: `?appointmentId=${appointmentId}`,
            },
          },
        });
        return;
      }

      if (response?.status !== 200 || !payload?.roomUrl || !payload?.token) {
        setError(
          response?.data?.error ||
            response?.data?.message ||
            "Failed to initialize video consultation. Please try again.",
        );
        setIsJoining(false);
        return;
      }

      try {
        await callObject.join({
          url: payload.roomUrl,
          token: payload.token,
        });
      } catch (joinError) {
        setError(
          joinError?.message ||
            "Failed to initialize video consultation. Please try again.",
        );
        setIsJoining(false);
      }
    };

    initCall();

    return () => {
      isMounted = false;
      callObject.off("joined-meeting", handleJoined);
      callObject.off("participant-joined", syncParticipants);
      callObject.off("participant-updated", syncParticipants);
      callObject.off("participant-left", syncParticipants);
      callObject.off("track-started", syncParticipants);
      callObject.off("track-stopped", syncParticipants);
      callObject.off("left-meeting", handleLeft);
      callObject.off("error", handleError);
      callObject.leave().catch(() => {});
      callObject.destroy().catch(() => {});
    };
  }, [appointmentId, callObject, setupError, navigate]);

  const handleLeave = () => {
    if (callObject) {
      void callObject.leave().catch(() => {});
    }
    // Close this participant's row in the call log (NABH COP.7.a). Best-effort:
    // a tab that is closed rather than exited never reaches this.
    if (appointmentId) {
      leaveVideoConsultation(appointmentId).catch(() => {});
    }
    navigate("/bookings", { replace: true });
  };

  if (!callObject) {
    // No call object → no DailyProvider; render the same error layout.
    return (
      <div className="video-call-page">
        <header className="video-call-header">
          <img src="/logo192.png" alt="MyHealthVaults" />
          <span>Secure Video Consultation</span>
        </header>
        <main className="video-call-body">
          <div className="video-call-error">
            <h2>Unable to start the call</h2>
            <p>{error || setupError}</p>
            <button className="danger" type="button" onClick={handleLeave}>
              Back to bookings
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <DailyProvider callObject={callObject}>
      <VideoCallShell
        error={error}
        isJoining={isJoining}
        onLeave={handleLeave}
        participantsSnapshot={participantsSnapshot}
      />
    </DailyProvider>
  );
};

export default VideoCallPage;
